"use client";

import { c as _c } from "react/compiler-runtime";
import { addToast } from "@dc/ui";
import { EllipsisTooltip } from "@dc/ui/components";
import { stripMarkdown } from "@dc/utils";
import type { Message } from "ably";
import { useChannel } from "ably/react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef } from "react";
import { useNotifications } from "@/hooks/useNotifications/useNotifications";
import { isUsableUserChannelName } from "@/lib/ablyChannels";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { parseCrunchReadyMessage } from "@/lib/parseCrunchReadyMessage";
import { resolveDeepLinkHref } from "@/lib/resolveDeepLinkHref";
import { useUserChannel } from "../UserChannelProvider";
import type { NotificationData } from "./UserChannelSubscriber.interface";
const SILENT_NOTIFICATION_TITLES = new Set(["COMPARISON PROGRESS"]);
const processedMessageIds = new Set<string>();
const MESSAGE_ID_TTL_MS = 10000;
function markMessageProcessed(messageId: string): boolean {
  if (processedMessageIds.has(messageId)) {
    return false;
  }
  processedMessageIds.add(messageId);
  setTimeout(() => processedMessageIds.delete(messageId), MESSAGE_ID_TTL_MS);
  return true;
}
const UserChannelSubscription = ({
  channelName,
  children
}: {
  channelName: string;
  children: React.ReactNode;
}) => {
  const t = useTranslations();
  const {
    refetchNotifications
  } = useNotifications();
  const {
    build
  } = useOrgTeamPath();
  const presenceEnteredRef = useRef(false);
  const handleMessage = useCallback((message: Message) => {
    const data = message.data as NotificationData | undefined;
    if (!data?.title) return;
    const messageId = message.id ?? `${message.timestamp}-${data.title}`;
    if (!markMessageProcessed(messageId)) {
      return;
    }
    if (SILENT_NOTIFICATION_TITLES.has(data.title)) return;
    const rawDeepLink = data.links?.[0]?.url;
    const resolvedPath = rawDeepLink ? resolveDeepLinkHref(rawDeepLink, build) : undefined;
    const crunchReadyMessage = parseCrunchReadyMessage(data.type, data.title, data.description ?? "");
    addToast({
      title: data.title,
      description: crunchReadyMessage ? <>
            <EllipsisTooltip content={crunchReadyMessage.fileName} className="font-medium text-foreground" tooltipClassName="wrap-anywhere line-clamp-none" />
            <p className="mt-1">{crunchReadyMessage.status}</p>
          </> : stripMarkdown(data.description || ""),
      color: data.color,
      ...(crunchReadyMessage && {
        classNames: {
          base: "max-w-[calc(100vw-2rem)] sm:w-112",
          content: "min-w-0 items-start",
          wrapper: "min-w-0 flex-1",
          title: "mb-2"
        }
      }),
      ...(resolvedPath && {
        endContent: <Link href={resolvedPath} className="inline-flex h-8 shrink-0 items-center rounded-medium bg-primary/20 px-3 text-primary text-small hover:bg-primary/30">
              {t("view")}
            </Link>
      })
    });
    refetchNotifications();
  }, [refetchNotifications, build, t]);
  const {
    channel
  } = useChannel({
    channelName,
    skip: false
  }, "in-app-notification", handleMessage);
  useEffect(() => {
    if (presenceEnteredRef.current) {
      return;
    }
    presenceEnteredRef.current = true;
    channel.presence.enter();
    return () => {
      presenceEnteredRef.current = false;
      try {
        const leaveResult = channel.presence.leave();
        if (leaveResult && typeof leaveResult.catch === "function") {
          leaveResult.catch(() => {
            // Channel may already be closed when unmounting from authenticated pages
          });
        }
      } catch (_error) {
        // Channel may already be closed when unmounting from authenticated pages
      }
    };
  }, [channel]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      try {
        if (document.hidden) {
          presenceEnteredRef.current = false;
          const leaveResult_0 = channel.presence.leave();
          if (leaveResult_0 && typeof leaveResult_0.catch === "function") {
            leaveResult_0.catch(() => {
              // Channel may already be closed
            });
          }
        } else if (!presenceEnteredRef.current) {
          presenceEnteredRef.current = true;
          const enterResult = channel.presence.enter();
          if (enterResult && typeof enterResult.catch === "function") {
            enterResult.catch(() => {
              // Channel may already be closed
            });
          }
        }
      } catch (_error_0) {
        // Channel may already be closed
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [channel]);
  return <>{children}</>;
};
export const UserChannelSubscriber = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  const {
    channelName
  } = useUserChannel();
  if (!isUsableUserChannelName(channelName)) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== channelName || $[3] !== children) {
    t1 = <UserChannelSubscription channelName={channelName}>{children}</UserChannelSubscription>;
    $[2] = channelName;
    $[3] = children;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};